import React, {
 forwardRef, useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { Dropdown } from 'react-bootstrap';

import qapi from 'views_shared/app/javascript/api';
import qlog from 'views_shared/app/javascript/scripts/qlog';
import buildPitchesText from './buildPitchesText';
import PitchCard from './PitchCard';
import PitchFilters, { DEFAULT_FILTERS, filtersActive, matchesFilters } from './PitchFilters';
import PitchInboxRail from './PitchInboxRail';
import ReplyAllModal from './ReplyAllModal';
import PitchThreadPane from '../../my_inbox/PitchThreadPane';
import withV2ProfileSlug from '../../my_inbox/withV2ProfileSlug';

const SORT_OPTIONS = [
  ['newest', 'Newest first'],
  ['oldest', 'Oldest first'],
];

// Ready transcripts of the selected pitches' videos, keyed by pitch id (best-effort: a pitch
// whose transcript is pending/failed simply gets no entry).
async function fetchVideoTranscripts(pitches) {
  const entries = await Promise.all(
    pitches.map(async (pitch) => {
      const video = (pitch.pitch_attachments || []).find((a) => a.type === 'Interact::Attachment::Video');
      if (!video) return null;
      const { data } = await qapi.attachments.transcriptStatus(video.id);
      const transcript = data?.transcript;
      if (transcript?.status !== 'ready' || !transcript.segments?.length) return null;
      return [pitch.id, transcript.segments];
    }),
  );
  return Object.fromEntries(entries.filter(Boolean));
}

// Caret-less toggle for the Sort dropdown, styled per the Figma controls row
// Tinted while open or once a sort is chosen, like the filter chips
const SortToggle = forwardRef(({
  onClick, label, active, 'aria-expanded': expanded,
}, ref) => (
  <button
    type="button"
    ref={ref}
    className={`pi-sort-btn ${active || expanded ? 'active' : ''}`}
    onClick={onClick}
    aria-expanded={expanded}
  >
    {label} <i className="fa-solid fa-chevron-down" style={{ fontSize: 14 }} />
  </button>
));
SortToggle.displayName = 'SortToggle';
SortToggle.propTypes = {
  onClick: PropTypes.func,
  label: PropTypes.string,
  active: PropTypes.bool,
  'aria-expanded': PropTypes.bool,
};

// The self-contained "Your Pitches" section (per the "New Pitch Inbox" Figma designs):
// owns the pitch + conversation data, the Table ⇄ Inbox toggle, tabs, and controls.
// The rail lists PITCHES — a pitch has no conversation until the reporter replies.
function PitchInboxSection({
  request, pitches: initialPitches = null, muteReport = null, canReply = true,
}) {
  const currentProfileId = globalThis.Qwoted.CURRENT_PROFILE_ID;
  // The request's V2 row names the owner media_profile_id; conversations are counted against it, not the viewer
  const [ownerProfileId, setOwnerProfileId] = useState(null);
  const viewerIsOwner = String(currentProfileId) === String(ownerProfileId);
  const [pitches] = useState(initialPitches); // V1: server props (mute/audit-filtered); no client fetch
  const [v2IdByV1Id, setV2IdByV1Id] = useState({});
  // V2 owns pitch attachments (videos exist only V2-side), so they ride the id bridge
  const [v2AttachmentsByV1Id, setV2AttachmentsByV1Id] = useState({});
  const [reactionByV1Id, setReactionByV1Id] = useState({}); // V2 owns the mark; keyed back to V1 ids
  const [hasVideoByV1Id, setHasVideoByV1Id] = useState({});
  const [view, setView] = useState('table');
  const [activeTab, setActiveTab] = useState('all');
  const [selectedPitchId, setSelectedPitchId] = useState(null);
  const [conversationsByPitchId, setConversationsByPitchId] = useState({});
  // Until the bridge answers, a messaged pitch is indistinguishable from an unmessaged one —
  // cards hold their Reply/quick-reply affordances so no one replies to the "wrong" state.
  const [conversationsLoaded, setConversationsLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [videoTranscriptByPitchId, setVideoTranscriptByPitchId] = useState({});
  const [railSort, setRailSort] = useState(null); // null = untouched default (newest)
  const [threadOpen, setThreadOpen] = useState(false); // mobile: rail ⇄ thread
  // Pitches the user explicitly opened THIS visit. The server bulk-marks everything read
  // on page load (props predate that), so the unread dot means "new since last visit" —
  // selecting a pitch clears it locally; no per-pitch read API needed.
  const [seenAt, setSeenAt] = useState({});
  // Mute/report results lifted here so every card, row, and thread menu stays in
  // sync across the Table/Inbox tabs — mutes keyed by user (they cover all of a
  // user's pitches), reports keyed by pitch
  const [mutedByUserId, setMutedByUserId] = useState({});
  const [reportedPitchIds, setReportedPitchIds] = useState({});
  const [showReplyAll, setShowReplyAll] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // Email links end in #pitch-<V1 id>: show that card in Table View on the All tab once the list is in
  const [anchorPitchId, setAnchorPitchId] = useState(() => /^#pitch-(\d+)$/.exec(window.location.hash)?.[1] || null);

  const pitchIds = useMemo(() => (pitches || []).map((p) => p.id), [pitches]);

  // V1 pitch ids are V2 migrate_ids: resolve the V2 rows once, then join conversations
  // (and pitcher locations) in V2 terms while keying results back to V1 ids.
  useEffect(() => {
    if (!pitchIds.length) return;
    qapi.pitches.getByMigrateIds(pitchIds).then(({ data, error }) => {
      if (error) {
        qlog.error('Failed to bridge pitch ids:', error);
        setConversationsLoaded(true); // degraded but not locked out
        return;
      }
      if (!(data || []).length) setConversationsLoaded(true); // no V2 twins yet (sync lag)
      const map = {};
      const attachments = {};
      const reactions = {};
      const hasVideo = {};
      (data || []).forEach((row) => {
        map[row.id] = { v1Id: row.migrate_id, profileId: row.profile_id };
        if (row.pitch_attachments?.length) { attachments[row.migrate_id] = row.pitch_attachments; }
        reactions[row.migrate_id] = row.reaction || null;
        if (row.has_video) { hasVideo[row.migrate_id] = true; }
      });
      setV2IdByV1Id(map);
      setV2AttachmentsByV1Id(attachments);
      setReactionByV1Id(reactions);
      setHasVideoByV1Id(hasVideo);
    }).catch((err) => {
      // A rejected bridge (not just an {error} result) must not wedge reply gating
      qlog.error('Pitch id bridge rejected:', err);
      setConversationsLoaded(true);
    });
  }, [pitchIds]);

  // V1 request ids are V2 migrate_ids: the V2 row carries the owner the conversations belong to
  useEffect(() => {
    qapi.requests.getByMigrateId(request.id).then(({ data, error }) => {
      const owner = data?.[0]?.media_profile_id;
      if (error || !owner) {
        if (error) { qlog.error('Failed to resolve the request owner:', error); }
        setConversationsLoaded(true); // degraded but not locked out
        return;
      }
      setOwnerProfileId(owner);
    }).catch((err) => {
      qlog.error('Request owner lookup rejected:', err);
      setConversationsLoaded(true);
    });
  }, [request.id]);

  const fetchConversations = useCallback(async () => {
    const v2Ids = Object.keys(v2IdByV1Id);
    if (!ownerProfileId || !v2Ids.length) return;
    // The owner's conversations whoever is looking; RLS decides who may read them (owner, their team, admins)
    const { data, error } = await qapi.conversations.getByPitchOriginIds(ownerProfileId, v2Ids);
    // Loaded either way — on error the cards stay actionable rather than lock reporters out
    setConversationsLoaded(true);
    if (error) {
      qlog.error('Failed to load pitch conversations:', error);
      return;
    }
    const byPitchId = {};
    (data || []).forEach((row) => {
      const bridge = v2IdByV1Id[row.origin_id];
      if (bridge) byPitchId[bridge.v1Id] = row;
    });
    setConversationsByPitchId(byPitchId);
  }, [ownerProfileId, v2IdByV1Id]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Same contract as my_inbox's markSelectedAsRead: server call + local receipt stamp,
  // guarded so scroll events on an already-read thread don't re-fire it.
  const handleMarkRead = useCallback((conversationId) => {
    setConversationsByPitchId((prev) => {
      const entry = Object.entries(prev).find(([, row]) => row.conversation_id === conversationId);
      if (!entry || entry[1].receipt_read_at) return prev;
      qapi.conversations.markReadById(conversationId).catch((err) => {
        qlog.error('Failed to mark conversation as read:', err);
      });
      return { ...prev, [entry[0]]: { ...entry[1], receipt_read_at: new Date().toISOString() } };
    });
  }, []);

  const toggleSelect = useCallback((pitchId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(pitchId)) {
        next.delete(pitchId);
      } else {
        next.add(pitchId);
      }
      return next;
    });
  }, []);

  // V2 owns the videos and the like/dislike mark: merge both onto the V1 rows by V1 id
  const pitchesWithAttachments = useMemo(
    () => (pitches || []).map((p) => {
      let out = v2AttachmentsByV1Id[p.id] ? { ...p, pitch_attachments: v2AttachmentsByV1Id[p.id] } : p;
      if (reactionByV1Id[p.id] !== undefined && reactionByV1Id[p.id] !== (out.reaction ?? null)) {
        out = { ...out, reaction: reactionByV1Id[p.id] };
      }
      if (hasVideoByV1Id[p.id]) { out = { ...out, has_video: true }; }
      return out;
    }),
    [pitches, v2AttachmentsByV1Id, reactionByV1Id, hasVideoByV1Id],
  );

  // Search reaches into what was said on video: fetch each ready transcript once the videos are known.
  // (Download Text re-fetches on demand, so a transcript that finishes later still reaches the export.)
  useEffect(() => {
    if (!pitchesWithAttachments.length) { return undefined; }
    let cancelled = false;
    fetchVideoTranscripts(pitchesWithAttachments).then((segmentsByPitchId) => {
      if (cancelled) { return; }
      setVideoTranscriptByPitchId(
        Object.fromEntries(
          Object.entries(segmentsByPitchId).map(([id, segments]) => [id, segments.map((seg) => seg.text).join(' ')]),
        ),
      );
    });
    return () => {
      cancelled = true;
    };
  }, [pitchesWithAttachments]);

  const visiblePitches = useMemo(() => {
    let list = pitchesWithAttachments;
    if (activeTab === 'messaged') list = list.filter((p) => conversationsByPitchId[p.id]);
    if (activeTab === 'shortlisted') list = list.filter((p) => p.reaction === 'like');
    if (filtersActive(filters)) { list = list.filter((p) => matchesFilters(p, filters)); }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (p) => (p.profile_name || '').toLowerCase().includes(q)
          || (p.pitch_text || '').toLowerCase().includes(q)
          || (p.profile_workplace || '').toLowerCase().includes(q)
          || (videoTranscriptByPitchId[p.id] || '').toLowerCase().includes(q),
      );
    }
    const sorted = [...list];
    // Audit-flagged pitchers sort last regardless of the date sort (V1 parity)
    sorted.sort((a, b) => {
      if (!!a.under_audit !== !!b.under_audit) return a.under_audit ? 1 : -1;
      return (railSort ?? 'newest') === 'newest'
        ? new Date(b.sent_at || 0) - new Date(a.sent_at || 0)
        : new Date(a.sent_at || 0) - new Date(b.sent_at || 0);
    });
    return sorted.map((p) => {
      let out = !p.read_at && seenAt[p.id] ? { ...p, read_at: seenAt[p.id] } : p;
      const muted = mutedByUserId[p.user_migrate_id];
      if (muted !== undefined && muted !== !!out.muted) out = { ...out, muted };
      if (reportedPitchIds[p.id] && !out.reported) out = { ...out, reported: true };
      return out;
    });
  }, [pitchesWithAttachments, activeTab, conversationsByPitchId, filters, search, railSort, seenAt,
    mutedByUserId, reportedPitchIds, videoTranscriptByPitchId]);

  const syncedMuteReport = useMemo(() => (muteReport ? {
    ...muteReport,
    onMutedChange: (userId, muted) => setMutedByUserId((prev) => ({ ...prev, [userId]: muted })),
    onReported: (pitchId) => setReportedPitchIds((prev) => ({ ...prev, [pitchId]: true })),
  } : null), [muteReport]);

  useEffect(() => {
    if (!anchorPitchId || !pitches?.length) {
      return;
    }
    if (!pitches.some((p) => String(p.migrate_id ?? p.id) === anchorPitchId)) {
      setAnchorPitchId(null);
      return;
    }
    setView('table');
    setActiveTab('all');
  }, [anchorPitchId, pitches]);

  useEffect(() => {
    if (!anchorPitchId) {
      return undefined;
    }
    const card = document.getElementById(`pitch-${anchorPitchId}`);
    if (!card) {
      return undefined;
    }
    // Cards above keep growing for a moment (conversation banners, attachments): follow it until the list settles
    const done = () => setAnchorPitchId(null);
    const observer = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(() => card.scrollIntoView({ block: 'start' }));
    card.scrollIntoView({ block: 'start' });
    observer?.observe(card.parentElement);
    const settle = setTimeout(done, 2000);
    const interactions = ['wheel', 'touchstart', 'keydown'];
    interactions.forEach((evt) => window.addEventListener(evt, done, { once: true, passive: true }));
    return () => {
      observer?.disconnect();
      clearTimeout(settle);
      interactions.forEach((evt) => window.removeEventListener(evt, done));
    };
  }, [anchorPitchId, visiblePitches, view, activeTab]);

  const totalCount = (pitches || []).length;
  const messagedCount = useMemo(
    () => (pitches || []).filter((p) => conversationsByPitchId[p.id]).length,
    [pitches, conversationsByPitchId],
  );
  const shortlistedCount = useMemo(
    () => pitchesWithAttachments.filter((p) => p.reaction === 'like').length,
    [pitchesWithAttachments],
  );

  const emptyTab = activeTab === 'shortlisted'
    ? { title: 'No liked pitches yet', hint: 'Like a pitch and it will show up here.' }
    : { title: 'No messaged pitches yet', hint: 'Reply to a pitch and it will show up here.' };
  let emptyState = emptyTab;
  if (filtersActive(filters)) {
    emptyState = { title: 'No pitches match your filters', hint: 'Change or clear the filters above.', clearable: true };
  } else if (search) {
    emptyState = { title: 'No pitches match your search', hint: 'Try a different search term.' };
  }

  // Optimistic: the thumb flips at once; a failed save puts the previous mark back, unless a
  // later click has already moved it on. Needs the V2 twin (the mark lives there), so the
  // thumbs stay inert until the bridge resolves.
  const v2IdFor = (v1Id) => Object.keys(v2IdByV1Id).find((key) => v2IdByV1Id[key].v1Id === v1Id);
  const handleReact = (v1Id, reaction) => {
    const v2Id = v2IdFor(v1Id);
    if (!v2Id) return;
    const previous = reactionByV1Id[v1Id] ?? null;
    const apply = (value, onlyIf) => setReactionByV1Id((prev) => (
      onlyIf === undefined || (prev[v1Id] ?? null) === onlyIf ? { ...prev, [v1Id]: value } : prev
    ));
    apply(reaction);
    qapi.pitches.setReactionById(v2Id, reaction).then(({ error }) => {
      if (error) {
        qlog.error('Failed to save pitch reaction:', error);
        apply(previous, reaction);
      }
    });
  };

  const selectedPitch = visiblePitches.find((p) => p.id === selectedPitchId)
    || (visiblePitches.length ? visiblePitches[0] : null);

  // seenAt is the optimistic layer; the V2 stamp (synced back to V1) is what survives a refresh.
  const markSeen = (id) => {
    setSeenAt((prev) => (prev[id] ? prev : { ...prev, [id]: new Date().toISOString() }));
    const pitch = (pitches || []).find((p) => p.id === id);
    const v2Id = v2IdFor(id);
    if (!viewerIsOwner || !pitch || pitch.read_at || seenAt[id] || !v2Id) return;
    qapi.pitches.markReadById(v2Id).then(({ error }) => {
      if (error) {
        qlog.error('Failed to mark pitch as read:', error);
      }
    });
  };

  const handleSelect = (id) => {
    setSelectedPitchId(id);
    setThreadOpen(true);
    markSeen(id);
    // A messaged pitch is a conversation row, whose dot reads the receipt rather than seenAt.
    // The global inbox clears it on select (useConversations#selectConversation -> markAsRead);
    // without this the dot survived opening the thread, which design QA caught.
    const conversation = conversationsByPitchId[id];
    if (conversation && viewerIsOwner) {
      handleMarkRead(conversation.conversation_id);
    }
  };

  // "View Conversation" normally opens the viewer's own inbox in a new tab, which only works
  // for the reporter the thread belongs to. Staff reading someone else's request page get the
  // thread in this page's Inbox view instead — the same pane, already scoped to the owner.
  const handleViewConversation = (id) => {
    setView('inbox');
    handleSelect(id);
  };

  if (pitches === null) {
    return (
      <div id="pitches-section" className="pitch-inbox text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading pitches...</span>
        </div>
      </div>
    );
  }

  return (
    <div id="pitches-section" className="pitch-inbox">
      <div className="pi-heading-row mb-3">
        <h5 className="mb-0">Your Pitches</h5>
        <div className="pi-view-toggle" role="group" aria-label="Pitch view">
          <button type="button" className={view === 'table' ? 'active' : ''} onClick={() => setView('table')}>
            <i className="fa-solid fa-list" /> Table View
          </button>
          <button type="button" className={view === 'inbox' ? 'active' : ''} onClick={() => setView('inbox')}>
            <i className="fa-regular fa-message" /> Inbox View
          </button>
        </div>
      </div>

      <div className="pi-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'all'}
          className={`pi-tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          All <span className="pi-tab-count">({totalCount})</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'messaged'}
          className={`pi-tab ${activeTab === 'messaged' ? 'active' : ''}`}
          onClick={() => setActiveTab('messaged')}
        >
          Messaged <span className="pi-tab-count">({messagedCount})</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'shortlisted'}
          className={`pi-tab ${activeTab === 'shortlisted' ? 'active' : ''}`}
          onClick={() => setActiveTab('shortlisted')}
        >
          Liked Pitches <span className="pi-tab-count">({shortlistedCount})</span>
        </button>
      </div>

      {selectedIds.size > 0 && (
        <div className="pi-controls pi-selection-bar">
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="pi-clear-selection"
              aria-label="Clear selection"
              onClick={() => setSelectedIds(new Set())}
            >
              <i className="fa-solid fa-xmark" />
            </button>
            <span className="pi-selected-count">
              <strong>Selected:</strong> {selectedIds.size}
            </span>
          </div>
          <div className="pi-controls-right">
            {visiblePitches.every((p) => selectedIds.has(p.id)) ? (
              <button type="button" className="pi-sort-btn" onClick={() => setSelectedIds(new Set())}>
                Deselect All
              </button>
            ) : (
              <button
                type="button"
                className="pi-sort-btn"
                onClick={() => setSelectedIds(new Set(visiblePitches.map((p) => p.id)))}
              >
                Select All
              </button>
            )}
            <button
              type="button"
              className="pi-download-btn"
              onClick={async () => {
                const selected = visiblePitches.filter((p) => selectedIds.has(p.id));
                const transcriptsByPitchId = await fetchVideoTranscripts(selected);
                const text = buildPitchesText(request, selected, conversationsByPitchId, transcriptsByPitchId);
                const blob = new Blob([text], {
                  type: 'text/plain',
                });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = `${request.slug || 'pitches'}-selected.txt`;
                link.click();
                URL.revokeObjectURL(link.href);
              }}
            >
              <i className="fa-solid fa-download" /> Download Selected Text
            </button>
          </div>
        </div>
      )}

      <div className={`pi-controls ${selectedIds.size > 0 ? 'd-none' : ''}`}>
        <button
          type="button"
          className="pi-reply-all"
          disabled={totalCount === 0 || !canReply}
          title={canReply ? undefined : 'Only reporters can reply to pitches'}
          onClick={() => setShowReplyAll(true)}
        >
          <i className="fa-solid fa-reply" /> Reply All
        </button>
        <div className="pi-controls-right">
          <Dropdown align="end">
            <Dropdown.Toggle
              as={SortToggle}
              label={railSort ? SORT_OPTIONS.find(([key]) => key === railSort)[1] : 'Sort by'}
              active={railSort !== null}
            />
            <Dropdown.Menu>
              {SORT_OPTIONS.map(([key, label]) => (
                <Dropdown.Item key={key} active={(railSort ?? 'newest') === key} onClick={() => setRailSort(key)}>
                  {label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <label className="pi-search mb-0">
            <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
            <input
              type="search"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search pitches"
            />
          </label>
          <button
            type="button"
            className={`pi-filter-btn ${filtersOpen || filtersActive(filters) ? 'active' : ''}`}
            title="Filters"
            aria-label="Filters"
            aria-pressed={filtersOpen}
            onClick={() => setFiltersOpen((open) => !open)}
          >
            <i className="fa-regular fa-sliders" />
          </button>
        </div>
      </div>
      {filtersOpen && selectedIds.size === 0 && <PitchFilters filters={filters} onChange={setFilters} />}

      {/* V1's page-level note above the pitches list (reporter_requests/show) */}
      <div className="alert pi-reply-note">
        <i className="fa-regular fa-circle-info" /> <strong>Note:</strong> To ensure the authenticity and expertise of
        individuals, Qwoted recommends arranging a video interview as part of the verification process.
      </div>

      {totalCount === 0 && (
        <div className="pi-card pi-empty-card text-center py-5">
          <div>
            <i className="fa-regular fa-inbox fa-3x text-secondary mb-3" />
            <h5 className="text-secondary">No pitches yet</h5>
            <p className="text-secondary mb-0">Pitches for this request will appear here</p>
          </div>
        </div>
      )}

      {totalCount > 0 && view === 'table' && (
        <div>
          {visiblePitches.length === 0 && (
            <div className="pi-card pi-empty-card text-center py-5">
              <div>
                <i className="fa-regular fa-inbox fa-3x text-secondary mb-3" />
                <h5 className="text-secondary">{emptyState.title}</h5>
                <p className="text-secondary mb-0">{emptyState.hint}</p>
                {emptyState.clearable && (
                  <button type="button" className="btn btn-link p-0 mt-2" onClick={() => setFilters(DEFAULT_FILTERS)}>
                    Clear filters
                  </button>
                )}
              </div>
            </div>
          )}
          {visiblePitches.map((pitch) => (
            <div key={pitch.id} id={`pitch-${pitch.migrate_id ?? pitch.id}`}>
              <PitchCard
                pitch={pitch}
                conversation={conversationsByPitchId[pitch.id] || null}
                request={request}
                location={pitch.sent_from || null}
                selected={selectedIds.has(pitch.id)}
                onToggleSelect={toggleSelect}
                muteReport={syncedMuteReport}
                canReply={canReply && conversationsLoaded}
                onReplied={fetchConversations}
                onReact={canReply && v2IdFor(pitch.id) ? handleReact : null}
                onViewConversation={viewerIsOwner ? null : handleViewConversation}
              />
            </div>
          ))}
        </div>
      )}

      <ReplyAllModal
        show={showReplyAll}
        onHide={() => setShowReplyAll(false)}
        request={request}
        pitchCount={totalCount}
        onReplied={fetchConversations}
      />

      {totalCount > 0 && view === 'inbox' && (
        <div className={`pi-card ${threadOpen ? 'thread-open' : ''}`}>
          <PitchInboxRail
            muteReport={syncedMuteReport}
            pitches={visiblePitches}
            conversationsByPitchId={conversationsByPitchId}
            selectedPitchId={selectedPitch?.id || null}
            onSelect={handleSelect}
            onActionComplete={fetchConversations}
            requestTitle={request.title}
            requestCreatedAt={request.created_at}
          />
          {selectedPitch ? (
            // Auto-selection doesn't mark a pitch seen — interacting with its thread does.
            // display: contents keeps the rail/pane flex layout untouched.
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions
            <div
              style={{ display: 'contents' }}
              onClickCapture={() => markSeen(selectedPitch.id)}
              onFocusCapture={() => markSeen(selectedPitch.id)}
            >
              <PitchThreadPane
                key={selectedPitch.id}
                pitch={selectedPitch}
                v2PitchId={
                  Number(
                    Object.keys(v2IdByV1Id).find((v2Id) => v2IdByV1Id[v2Id].v1Id === selectedPitch.id),
                  ) || null
                }
                conversation={conversationsByPitchId[selectedPitch.id] || null}
                request={request}
                onConversationCreated={fetchConversations}
                onMarkRead={viewerIsOwner ? handleMarkRead : undefined}
                readProfileId={viewerIsOwner ? null : ownerProfileId}
                onActionComplete={fetchConversations}
                onBack={() => setThreadOpen(false)}
                muteReport={syncedMuteReport}
                canReply={canReply}
              />
            </div>
          ) : (
            <div className="pi-empty">Reply to a pitch to move it into your inbox.</div>
          )}
        </div>
      )}
    </div>
  );
}

PitchInboxSection.propTypes = {
  request: PropTypes.shape({
    id: PropTypes.number.isRequired,
    slug: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
  // Server props from reporter_requests/show: V1-filtered (mutes, audit) + serialized
  // eslint-disable-next-line react/forbid-prop-types -- server-serialized passthrough
  pitches: PropTypes.array,
  // Wiring for V1's mute/report flows (reporting user id, mute duration, behavior types)
  // eslint-disable-next-line react/forbid-prop-types -- server-serialized passthrough
  muteReport: PropTypes.object,
  // False for internal/admin viewers (V1 parity: only reporters can reply)
  canReply: PropTypes.bool,
};

export { PitchInboxSection };

export default withV2ProfileSlug(PitchInboxSection);
